import type {
  AiScriptRerunRunOptions,
  AiScriptRerunSelection,
  AiScriptRerunState,
  AiScriptRerunVersionInfo,
  PayBreakdownVersionContext,
} from "@/payroll/pay-breakdown/types";
import { AI_SCRIPT_RERUN_VERSION_CHOICE } from "@/payroll/pay-breakdown/types";
import type { EnrichedPayPeriod } from "@/payroll/payroll.d";

export const getPayBreakdownVersionContext = (
  selectedPayPeriod: EnrichedPayPeriod | undefined,
  payPeriods: EnrichedPayPeriod[],
): PayBreakdownVersionContext => {
  const rosterVersionNumber = selectedPayPeriod?.roster_version_number ?? null;
  const currentRosterVersionNumber =
    selectedPayPeriod?.current_roster_version_number ??
    payPeriods.find((period) => period.current_roster_version_number != null)
      ?.current_roster_version_number ??
    null;
  const currentScriptVersionNumber =
    selectedPayPeriod?.current_ai_script_version_number ??
    payPeriods.find((period) => period.current_ai_script_version_number != null)
      ?.current_ai_script_version_number ??
    null;

  return {
    rosterVersionNumber,
    currentRosterVersionNumber,
    rerunVersionInfo: {
      runScriptVersion: selectedPayPeriod?.ai_script_version_number ?? null,
      runRosterVersion: rosterVersionNumber,
      currentScriptVersion: currentScriptVersionNumber,
      currentRosterVersion: currentRosterVersionNumber,
      runScriptVersionId:
        selectedPayPeriod?.latest_completed_ai_script_run_version_id ?? null,
      runRosterId:
        selectedPayPeriod?.latest_completed_ai_script_run_roster_id ?? null,
    },
  };
};

export const getAiScriptRerunState = (
  versionInfo: AiScriptRerunVersionInfo,
): AiScriptRerunState => {
  const scriptDrift =
    versionInfo.runScriptVersion != null &&
    versionInfo.currentScriptVersion != null &&
    versionInfo.runScriptVersion !== versionInfo.currentScriptVersion;
  const rosterDrift =
    versionInfo.runRosterVersion != null &&
    versionInfo.currentRosterVersion != null &&
    versionInfo.runRosterVersion !== versionInfo.currentRosterVersion;

  return {
    scriptDrift,
    rosterDrift,
    hasDrift: scriptDrift || rosterDrift,
    scriptVersion:
      versionInfo.runScriptVersion ?? versionInfo.currentScriptVersion,
    rosterVersion:
      versionInfo.runRosterVersion ?? versionInfo.currentRosterVersion,
  };
};

export const getMissingAiScriptRerunVersionReferences = ({
  versionInfo,
  scriptChoice,
  rosterChoice,
  isScopedRerun,
}: AiScriptRerunSelection) => {
  const { scriptDrift, rosterDrift } = getAiScriptRerunState(versionInfo);

  return [
    ...(scriptDrift &&
    (isScopedRerun || scriptChoice === AI_SCRIPT_RERUN_VERSION_CHOICE.keep) &&
    versionInfo.runScriptVersionId === null
      ? ["script"]
      : []),
    ...(rosterDrift &&
    rosterChoice === AI_SCRIPT_RERUN_VERSION_CHOICE.keep &&
    versionInfo.runRosterId === null
      ? ["roster"]
      : []),
  ];
};

export const buildAiScriptRerunOptions = (
  selection: AiScriptRerunSelection,
): AiScriptRerunRunOptions | undefined => {
  const { versionInfo, scriptChoice, rosterChoice, isScopedRerun } = selection;
  const { scriptDrift, rosterDrift, hasDrift } =
    getAiScriptRerunState(versionInfo);
  if (!hasDrift) return undefined;

  const missingVersionReferences =
    getMissingAiScriptRerunVersionReferences(selection);
  if (missingVersionReferences.length > 0) {
    throw new Error(
      `Cannot rerun payroll: missing historical ${missingVersionReferences.join(
        " and ",
      )} version reference`,
    );
  }

  return {
    ...(scriptDrift &&
    (isScopedRerun || scriptChoice === AI_SCRIPT_RERUN_VERSION_CHOICE.keep)
      ? { aiScriptVersionId: versionInfo.runScriptVersionId! }
      : {}),
    ...(rosterDrift && rosterChoice === AI_SCRIPT_RERUN_VERSION_CHOICE.keep
      ? { payrollRosterId: versionInfo.runRosterId! }
      : {}),
  };
};
